import { useCallback, useEffect, useRef, useState } from 'react';

// Generic data-fetching hook with loading/error states
export function useApi(fn, deps = [], opts = {}) {
  const { auto = true, initial = null } = opts;
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(auto);
  const [error, setError] = useState(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async (...args) => {
    setLoading(true); setError(null);
    try {
      const result = await fn(...args);
      if (mounted.current) setData(result);
      return result;
    } catch (e) {
      if (mounted.current) setError(e);
      throw e;
    } finally {
      if (mounted.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (auto) void run().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, refetch: run, setData };
}

export function useMutation(fn) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const call = async (...args) => {
    setLoading(true); setError(null);
    try { return await fn(...args); }
    catch (e) { setError(e); throw e; }
    finally { setLoading(false); }
  };
  return { call, loading, error };
}
